// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { useFormik } from "formik";
// COMPONENTS
import MUIDataTable from "mui-datatables";
import { Button, Modal, Form } from "react-bootstrap";
import { Link } from 'react-router-dom';
import SVG from "react-inlinesvg";
// CUSTOM COMPONENTS
import * as alert from "../../../redux/alertSnackBarRedux";
import * as confirmModal from "../../../redux/confirmModalRedux";
// SERVICES AND HELPERS
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import * as locationService from "../../../services/management/locationService";
import ClientLocationValidator from "../../../helpers/validators/ClientLocationValidator";
import { dashIfNull, getInputClasses } from "../../../helpers/helper";

const ClientLocationListingPage = (props) => {
    const { showAlert, showConfirmModal, hideConfirmModal, clientProfile } = props;
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        loadTableData();
    }, [])

    const loadTableData = () => {
        locationService.getLocations(clientProfile.id).then(res => {
            setData(res.map(x => { return { ...x.address, id: x.id, businessName: x.businessName, isPrimary: x.isPrimary } }));
        }).catch((ex) => {
            showAlert({ message: ex.message, variant: "error" });
        });
    }

    const deleteLocation = (id) => {
        locationService.deleteLocation(id).then(res => {
            showAlert({ message: res.message, variant: "success" });
            loadTableData();
            hideConfirmModal();
        })
    }

    const columns = [
        {
            name: "id",
            label: "Actions",
            options: {
                filter: false,
                sort: false,
                width: 1,
                print: false,
                download: false,
                setCellProps: () => ({
                    'data-aes-field': 'actions',
                    'data-aes-label': 'Actions'
                }),
                customBodyRenderLite: (dataIndex) => {
                    return (
                        <div className="aes-admin-listing-actions">
                            <Link
                                className="btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5"
                                to={"/management/client-detail/client-location/" + data[dataIndex].id}
                            >
                                View
                            </Link>
                            <button
                                onClick={() => showConfirmModal({
                                    title: "Delete Location",
                                    text: "Are you sure you want to delete this location?",
                                    rightBtnText: "Confirm",
                                    btnAction: () => deleteLocation(data[dataIndex].id)
                                })}
                                className="btn btn-sm btn-danger font-weight-bold py-2 px-3 px-xxl-5 my-1"
                            >
                                Delete
                            </button>
                        </div>
                    );
                }
            },

        },
        {
            name: "businessName",
            label: "Business Name",
            options: {
                filter: true,
                sort: true,
                setCellProps: () => ({
                    'data-aes-field': 'primary',
                    'data-aes-label': 'Business Name'
                }),
                customBodyRenderLite: (dataIndex) => {
                    return (
                        <p>{dashIfNull(data[dataIndex].businessName)}</p>
                    )
                }
            }
        },
        {
            name: "address1",
            label: "Address",
            options: {
                filter: true,
                sort: true,
                setCellProps: () => ({
                    'data-aes-field': 'detail',
                    'data-aes-label': 'Address'
                }),
            }
        },
        {
            name: "city",
            label: "City",
            options: {
                filter: true,
                sort: true,
                setCellProps: () => ({
                    'data-aes-field': 'secondary-one',
                    'data-aes-label': 'City'
                }),
            }
        },
        {
            name: "state",
            label: "State",
            options: {
                filter: true,
                sort: true,
                setCellProps: () => ({
                    'data-aes-field': 'secondary-two',
                    'data-aes-label': 'State'
                }),
            }
        },
        {
            name: "zipCode",
            label: "Zip Code",
            options: {
                filter: true,
                sort: true,
                setCellProps: () => ({
                    'data-aes-field': 'secondary-three',
                    'data-aes-label': 'Zip Code'
                }),
            }
        },
        {
            name: "isPrimary",
            label: "Is Primary?",
            options: {
                filter: true,
                sort: true,
                setCellProps: () => ({
                    'data-aes-field': 'status',
                    'data-aes-label': 'Is Primary?'
                }),
                customBodyRenderLite: (dataIndex) => {
                    return (
                        <>
                            <SVG
                                className="h-50 align-self-center"
                                src={toAbsoluteUrl(data[dataIndex].isPrimary  ? "/media/svg/icons/Navigation/Check.svg" : "/media/svg/icons/Navigation/Close.svg")}
                            >
                            </SVG>
                        </>
                    );
                }
            }
        }
    ];

    const handleModalClose = () => {
        setIsModalOpen(false);
    }


    return (
        <div className="row aes-admin-listing-page aes-admin-listing-page--client-locations" >
            <div className="col-lg-12 col-xxl-12">
                <div className={`card card-custom card-stretch gutter-b}`}>
                    <div className="card-header border-0 py-5">
                        <h3 className="card-title align-items-start flex-column">
                            <span className="card-label font-weight-bolder text-dark">
                                Locations for {clientProfile.name}
                            </span>
                            <span className="text-muted mt-3 font-weight-bold fz-xs">
                                View and Manage Client Locations
                            </span>
                        </h3>
                        <div className="card-toolbar">
                            <Link
                                to="/management/client-detail/client-location/0"
                                className="btn btn-success font-weight-bolder font-size-sm mr-3"
                            >
                                <span className="svg-icon svg-icon-lg svg-icon-white">
                                            <SVG
                                                src={toAbsoluteUrl(
                                                    "/media/svg/icons/Code/Plus.svg"
                                                )}
                                            />
                                        </span> Add Location
                            </Link>
                        </div>
                    </div>
                    <div className="container-fluid aes-admin-listing">
                        <MUIDataTable
                            className="aes-admin-listing-surface"
                            data={data}
                            columns={columns}
                            options={{
                                responsive: 'standard',
                                rowsPerPage: 25,
                                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                                selectableRows: 'none',
                                setTableProps: () => ({
                                    className: 'aes-admin-listing-table'
                                })
                            }}
                        />
                    </div>
                </div>
            </div>
        </div >
    )
}

const mapStateToProps = (state) => ({
    clientProfile: state.clientProfile
})

export default connect(mapStateToProps, { ...alert.actions, ...confirmModal.actions })(ClientLocationListingPage);
